import { ref, computed, Ref, ComputedRef } from 'vue';
import type { FlowStructureDto } from '@/types/flow-structure';

/**
 * Types of actions that can be recorded in history
 */
export type FlowActionType =
  | 'add_operator'
  | 'remove_operator'
  | 'update_operator'
  | 'replace_operator'
  | 'merge_branches'
  | 'unmerge_branches'
  | 'closure_branches'
  | 'reorder_branches'
  | 'batch'
  | 'initial'
  | 'save';

/**
 * Metadata for history entry - generic key-value params for flexible data storage
 * The actionType determines how to interpret these params
 */
export interface HistoryEntryMeta {
  operatorType?: string;
  operatorId?: string;
  oldOperatorType?: string;
  newOperatorType?: string;
  sourceOperatorType?: string;
  targetOperatorType?: string;
  [key: string]: string | undefined;
}

/**
 * A single entry in the history stack
 */
export interface HistoryEntry {
  id: string;
  timestamp: number;
  actionType: FlowActionType;
  snapshot: FlowStructureDto;
  meta?: HistoryEntryMeta;
}

/**
 * State structure for flow history
 */
export interface FlowHistoryState {
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
}

/**
 * Convert history state to a flat array for database storage
 */
export function historyStateToArray(state: FlowHistoryState): HistoryEntry[] {
  return [...state.undoStack, ...state.redoStack];
}

/**
 * Configuration options for useFlowHistory
 */
export interface FlowHistoryOptions {
  maxSize?: number;
  debounceMs?: number;
}

const DEFAULT_MAX_SIZE = 50;
const DEFAULT_DEBOUNCE_MS = 300;

/**
 * Generate a unique ID for history entries
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Deep clone a flow structure to avoid reference issues
 */
const cloneFlow = (flow: FlowStructureDto): FlowStructureDto => {
  return JSON.parse(JSON.stringify(flow));
};

/**
 * Composable for managing undo/redo history for a flow editor
 *
 * Features:
 * - Maintains separate undo and redo stacks
 * - Configurable max history size (default: 50)
 * - Debouncing for rapid updates (e.g., form field changes)
 * - Deep cloning to prevent reference issues
 *
 * Usage:
 * ```typescript
 * const history = useFlowHistory({ maxSize: 50 });
 *
 * // Before making a change, push current state
 * history.pushSnapshot(currentFlow, 'add_operator', 'Added Email action');
 *
 * // To undo
 * if (history.canUndo.value) {
 *   const previousState = history.undo();
 *   // Apply previousState to your flow
 * }
 * ```
 */
export function useFlowHistory(options: FlowHistoryOptions = {}) {
  const maxSize = options.maxSize ?? DEFAULT_MAX_SIZE;
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const undoStack: Ref<HistoryEntry[]> = ref([]);
  const redoStack: Ref<HistoryEntry[]> = ref([]);

  let lastPushTime = 0;
  let lastActionType: FlowActionType | null = null;

  const canUndo: ComputedRef<boolean> = computed(() => undoStack.value.length > 0);
  const canRedo: ComputedRef<boolean> = computed(() => redoStack.value.length > 0);

  const undoActionType: ComputedRef<FlowActionType | null> = computed(() => {
    if (undoStack.value.length === 0) return null;
    return undoStack.value[undoStack.value.length - 1].actionType;
  });

  const undoMeta: ComputedRef<HistoryEntryMeta | null> = computed(() => {
    if (undoStack.value.length === 0) return null;
    return undoStack.value[undoStack.value.length - 1].meta ?? null;
  });

  const redoActionType: ComputedRef<FlowActionType | null> = computed(() => {
    if (redoStack.value.length === 0) return null;
    return redoStack.value[redoStack.value.length - 1].actionType;
  });

  const redoMeta: ComputedRef<HistoryEntryMeta | null> = computed(() => {
    if (redoStack.value.length === 0) return null;
    return redoStack.value[redoStack.value.length - 1].meta ?? null;
  });

  const undoStackSize: ComputedRef<number> = computed(() => undoStack.value.length);
  const redoStackSize: ComputedRef<number> = computed(() => redoStack.value.length);

  /**
   * Push a snapshot of the current flow state onto the undo stack.
   * Call this BEFORE making a mutation to the flow.
   *
   * @param flow - The current flow state to save
   * @param actionType - The type of action being performed
   * @param meta - Optional metadata for the action (raw data for i18n)
   * @param forceNew - If true, bypasses debounce logic
   */
  const pushSnapshot = (
    flow: FlowStructureDto,
    actionType: FlowActionType,
    meta?: HistoryEntryMeta,
    forceNew = false,
  ): void => {
    const now = Date.now();

    // Debounce logic: if same action type within debounce window, update last entry
    if (
      !forceNew &&
      actionType === 'update_operator' &&
      lastActionType === 'update_operator' &&
      now - lastPushTime < debounceMs &&
      undoStack.value.length > 0
    ) {
      // Don't push a new entry, the previous snapshot is still valid
      // This prevents flooding the stack with rapid form changes
      return;
    }

    const entry: HistoryEntry = {
      id: generateId(),
      timestamp: now,
      actionType,
      snapshot: cloneFlow(flow),
      meta,
    };

    undoStack.value.push(entry);

    // Enforce max size limit
    if (undoStack.value.length > maxSize) {
      undoStack.value.shift();
    }

    // Clear redo stack on new action
    redoStack.value = [];

    lastPushTime = now;
    lastActionType = actionType;
  };

  /**
   * Undo the last action.
   * Returns the previous flow state to restore, or null if nothing to undo.
   *
   * @param currentFlow - The current flow state (will be pushed to redo stack)
   */
  const undo = (currentFlow: FlowStructureDto): FlowStructureDto | null => {
    if (undoStack.value.length === 0) return null;

    const lastEntry = undoStack.value.pop()!;

    // Push current state to redo stack
    const redoEntry: HistoryEntry = {
      id: generateId(),
      timestamp: Date.now(),
      actionType: lastEntry.actionType,
      snapshot: cloneFlow(currentFlow),
      meta: lastEntry.meta,
    };
    redoStack.value.push(redoEntry);

    return cloneFlow(lastEntry.snapshot);
  };

  /**
   * Redo the last undone action.
   * Returns the next flow state to restore, or null if nothing to redo.
   *
   * @param currentFlow - The current flow state (will be pushed to undo stack)
   */
  const redo = (currentFlow: FlowStructureDto): FlowStructureDto | null => {
    if (redoStack.value.length === 0) return null;

    const nextEntry = redoStack.value.pop()!;

    // Push current state to undo stack
    const undoEntry: HistoryEntry = {
      id: generateId(),
      timestamp: Date.now(),
      actionType: nextEntry.actionType,
      snapshot: cloneFlow(currentFlow),
      meta: nextEntry.meta,
    };
    undoStack.value.push(undoEntry);

    return cloneFlow(nextEntry.snapshot);
  };

  /**
   * Clear all history (both undo and redo stacks)
   */
  const clear = (): void => {
    undoStack.value = [];
    redoStack.value = [];
    lastPushTime = 0;
    lastActionType = null;
  };

  /**
   * Clear only the redo stack (used after save checkpoint)
   */
  const clearRedoStack = (): void => {
    redoStack.value = [];
  };

  /**
   * Get the current history state (for persistence/debugging)
   */
  const getState = (): FlowHistoryState => ({
    undoStack: undoStack.value.map((e) => ({ ...e, snapshot: cloneFlow(e.snapshot) })),
    redoStack: redoStack.value.map((e) => ({ ...e, snapshot: cloneFlow(e.snapshot) })),
  });

  /**
   * Restore history state (for persistence/debugging)
   */
  const setState = (state: FlowHistoryState): void => {
    undoStack.value = state.undoStack.map((e) => ({ ...e, snapshot: cloneFlow(e.snapshot) }));
    redoStack.value = state.redoStack.map((e) => ({ ...e, snapshot: cloneFlow(e.snapshot) }));
  };

  /**
   * Restore to a specific history entry by index in the undo stack.
   * This is equivalent to calling undo() multiple times until reaching that entry.
   * Returns the snapshot to restore to, or null if invalid.
   */
  const restoreToIndex = (index: number, currentFlow: FlowStructureDto): FlowStructureDto | null => {
    if (index < 0 || index >= undoStack.value.length) return null;

    // Calculate how many undos we need to perform
    const undoCount = undoStack.value.length - index;

    let currentState = cloneFlow(currentFlow);

    // Perform multiple undos
    for (let i = 0; i < undoCount; i++) {
      const result = undo(currentState);
      if (!result) break;
      currentState = result;
    }

    return currentState;
  };

  return {
    // State (readonly)
    canUndo,
    canRedo,
    undoActionType,
    undoMeta,
    redoActionType,
    redoMeta,
    undoStackSize,
    redoStackSize,
    undoStack: computed(() => undoStack.value),
    redoStack: computed(() => redoStack.value),

    // Actions
    pushSnapshot,
    undo,
    redo,
    clear,
    clearRedoStack,
    restoreToIndex,

    // Persistence helpers
    getState,
    setState,
  };
}

export type FlowHistory = ReturnType<typeof useFlowHistory>;
